import { ZoneContextManager } from '@opentelemetry/context-zone';
import {
  CompositePropagator,
  W3CBaggagePropagator,
  W3CTraceContextPropagator,
} from '@opentelemetry/core';
import {
  BatchSpanProcessor,
  StackContextManager,
  WebTracerProvider,
} from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { SpanStatusCode } from '@opentelemetry/api';
import { log } from '@domgen/shared/src/utils/log';

const getContextManager = () => {
  const hasZone =
    typeof globalThis !== 'undefined' &&
    typeof (globalThis as { Zone?: unknown }).Zone !== 'undefined';

  if (hasZone) {
    return new ZoneContextManager();
  }

  log.warn({
    message:
      '[APP WARNING] Zone.js global was not found. Falling back to StackContextManager for OpenTelemetry context propagation.',
  });

  return new StackContextManager();
};

/**
 * Initializes OpenTelemetry tracing for the web application.
 *
 * Configures and registers a WebTracerProvider with the following features:
 * - Sets up OTLP HTTP exporter for sending traces to LightStep
 * - Configures service name based on brand code and deployment environment
 * - Registers web auto-instrumentations for automatic span creation
 * - Sets up context propagation using W3C standards (Baggage and Trace Context)
 * - Applies custom attributes to fetch spans for synthetic request tracking
 *
 * Required environment variables:
 * - VITE_BRAND_CODE: Brand identifier (e.g., 'amana')
 * - VITE_OTEL_SERVICE_VERSION: Service version for resource attributes
 * - VITE_OTEL_EXPORTER_OTLP_ENDPOINT: OTLP exporter endpoint URL
 * - VITE_OTEL_LIGHTSTEP_TOKEN: Authentication token for LightStep
 * - MODE: Deployment environment ('production', 'development', etc.)
 *
 * If any required variables are missing, telemetry initialization is skipped
 * with a warning logged in non-production environments.
 *
 * @async
 * @param {string} [version] - Optional service version to override environment variable
 * @returns {Promise<void>} Resolves when telemetry initialization is complete or skipped
 * @example
 * ```
 * await tracer();
 * ```
 * @todo Consider adding error handling for exporter failures.
 * @todo Review and update resource attributes as per latest OpenTelemetry specifications.
 * @todo Potentially move to a separate telemetry module for better organization (lib).
 */
export const tracer = async ({
  version,
  brandCode,
  otelExporterEndpoint,
  ignoreUrls,
  environment,
  namespace,
  otelToken,
  traceParentCorsUrls,
}: {
  version?: string;
  namespace?: string;
  brandCode?: string;
  otelExporterEndpoint?: string;
  otelToken?: string;
  ignoreUrls?: (RegExp | string)[];
  traceParentCorsUrls?: (RegExp | string)[];
  environment?: string;
}): Promise<void> => {
  if (!brandCode || !version || !otelExporterEndpoint || !otelToken || !environment) {
    log.warn({
      message:
        '[APP WARNING] OpenTelemetry environment variables are not set correctly. Telemetry will not be initialized.',
    });

    return;
  } else {
    log.debug({
      message: 'OpenTelemetry environment variables found. Initializing telemetry...',
      data: {
        brandCode,
        version,
        otelExporterEndpoint,
        environment,
      },
    });

    const serviceName =
      `olr-${brandCode}-us-client${environment !== 'production' ? `-${environment}` : ''}`
        .trim()
        .toLocaleLowerCase();

    const providerWithZone = new WebTracerProvider({
      resource: resourceFromAttributes({
        // Properties are deprecated according to latest spec
        ['service.name']: serviceName,
        ['deployment.environment']: environment,
        ['service.version']: version ?? 'unknown', // we should use the package version here
        [`${namespace ? `${namespace}.` : ''}tenant`]: brandCode,
        ['service.kind']: 'client',
      }),
      spanProcessors: [
        new BatchSpanProcessor(
          new OTLPTraceExporter({
            url: otelExporterEndpoint,
            headers: {
              ['lightstep-access-token']: otelToken,
            },
          }),
        ),
      ],
    });

    providerWithZone.register({
      contextManager: getContextManager(),
      propagator: new CompositePropagator({
        propagators: [new W3CBaggagePropagator(), new W3CTraceContextPropagator()],
      }),
    });

    registerInstrumentations({
      tracerProvider: providerWithZone,
      instrumentations: [
        getWebAutoInstrumentations({
          '@opentelemetry/instrumentation-fetch': {
            enabled: true,
            ignoreNetworkEvents: false, // capture both request and response spans for fetch
            propagateTraceHeaderCorsUrls: traceParentCorsUrls,
            applyCustomAttributesOnSpan: (span, _, response) => {
              if (response.status && [400, 404, 500, 502, 503, 504].includes(response.status)) {
                span.setStatus({ code: SpanStatusCode.ERROR });
                span.setAttribute('http.status_code', response.status);
              }
            },
          },
          /** https://github.com/open-telemetry/opentelemetry-js-contrib/tree/main/packages/instrumentation-document-load */
          '@opentelemetry/instrumentation-document-load': {
            enabled: true,
          },
          /** https://github.com/open-telemetry/opentelemetry-js-contrib/tree/main/packages/instrumentation-user-interaction */
          '@opentelemetry/instrumentation-user-interaction': {
            enabled: true,
            // Restrict to high-signal events to balance visibility and overhead:
            // - 'click' and 'submit' capture primary user interactions with the UI
            // - 'load' and 'error' capture key lifecycle and failure events
            // This deviates from the broader default event set to avoid noisy spans
            // while still providing actionable interaction telemetry.
            eventNames: ['click', 'submit', 'load', 'error'],
          },
          '@opentelemetry/instrumentation-xml-http-request': {
            enabled: true,
            propagateTraceHeaderCorsUrls: traceParentCorsUrls,
            ignoreUrls,
            clearTimingResources: true,
          },
        }),
      ],
    });
  }
};
